import React, { useRef, useImperativeHandle, forwardRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import * as _twemojiModule from 'twemoji';
// twemoji v14 ESM: `export default twemoji`; CJS webpack interop may wrap it
const twemoji = _twemojiModule?.default || _twemojiModule;

const TWEMOJI_OPTS = {
  base: 'https://cdn.jsdelivr.net/gh/twitter/twemoji@14.0.2/assets/',
  folder: '72x72',
  ext: '.png',
  className: 'twemoji',
};

// ─── DOM helpers ─────────────────────────────────────────────────────────────

/** Reconstructs plain text from a contenteditable node.
 *  Text nodes → their text; <img.twemoji> → alt attribute (emoji char). */
const extractPlainText = (el) => {
  let text = '';
  el.childNodes.forEach((node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      text += node.textContent;
    } else if (node.nodeName === 'IMG') {
      text += node.alt;
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      text += extractPlainText(node);
    }
  });
  return text;
};

/** Move cursor to the very end of a contenteditable element. */
const moveCursorToEnd = (el) => {
  el.focus();
  const range = document.createRange();
  range.selectNodeContents(el);
  range.collapse(false);
  const sel = window.getSelection();
  sel.removeAllRanges();
  sel.addRange(range);
};

/**
 * Returns the cursor's START position as a character offset within the
 * plain text (emoji imgs count as 1 char each via their alt).
 */
const getCaretCharOffset = (el) => {
  const sel = window.getSelection();
  if (!sel || !sel.rangeCount) return 0;
  if (!el || !el.contains(sel.anchorNode)) return 0;

  const range = sel.getRangeAt(0);
  const preRange = range.cloneRange();
  preRange.selectNodeContents(el);
  preRange.setEnd(range.startContainer, range.startOffset);

  const tmp = document.createElement('div');
  tmp.appendChild(preRange.cloneContents());
  return extractPlainText(tmp).length;
};

/**
 * Returns the cursor's END position (same as start when no selection).
 */
const getCaretCharEnd = (el) => {
  const sel = window.getSelection();
  if (!sel || !sel.rangeCount) return 0;
  if (!el || !el.contains(sel.anchorNode)) return 0;

  const range = sel.getRangeAt(0);
  const preRange = range.cloneRange();
  preRange.selectNodeContents(el);
  preRange.setEnd(range.endContainer, range.endOffset);

  const tmp = document.createElement('div');
  tmp.appendChild(preRange.cloneContents());
  return extractPlainText(tmp).length;
};

/**
 * Sets the cursor to `targetOffset` (plain-text character index).
 * Handles both text nodes and <img> (twemoji) nodes.
 */
const setCaretAtOffset = (el, targetOffset) => {
  if (!el) return;
  let remaining = targetOffset;
  let placed = false;

  const place = (node, offset) => {
    const range = document.createRange();
    range.setStart(node, offset);
    range.collapse(true);
    const sel = window.getSelection();
    sel.removeAllRanges();
    sel.addRange(range);
    placed = true;
  };

  const traverse = (node) => {
    if (placed) return;

    if (node.nodeType === Node.TEXT_NODE) {
      const len = node.textContent.length;
      if (remaining <= len) {
        place(node, remaining);
      } else {
        remaining -= len;
      }
    } else if (node.nodeName === 'IMG') {
      if (remaining <= 0) {
        // Place cursor before this img
        const idx = Array.from(node.parentNode.childNodes).indexOf(node);
        place(node.parentNode, idx);
      } else {
        remaining -= 1;
      }
    } else {
      for (const child of Array.from(node.childNodes)) {
        traverse(child);
        if (placed) return;
      }
    }
  };

  traverse(el);
  if (!placed) moveCursorToEnd(el);
};

// ─── Component ───────────────────────────────────────────────────────────────

/**
 * TwemojiInput — a contenteditable message input that renders all emoji
 * characters as Twemoji <img> tags in real time.
 *
 * Exposed imperative API (via ref):
 *   insertEmoji(char)       — insert emoji at cursor, convert to Twemoji img
 *   insertAtCursor(text)    — insert plain text at cursor
 *   clear()                 — clear all content
 *   getText()               — return current plain text
 *   focus()                 — focus the input
 *   setValue(text, cur?)    — replace content; optionally set cursor offset
 *   getSelectionStart()     — cursor start as plain-text char offset
 *   getSelectionEnd()       — cursor end as plain-text char offset
 *   setCaretPosition(n)     — move cursor to plain-text offset n
 *   getSelectedText()       — plain text currently selected
 *   deleteSelection()       — delete selected content
 *
 * Callbacks:
 *   onTextChange(text, cursorStart) — fired on every input
 *   onEnterPress()                  — fired on Enter (not Shift+Enter)
 *   onKeyDown(e)                    — fired on every keydown BEFORE built-in handling
 */
const TwemojiInput = forwardRef(({
  onTextChange,
  onEnterPress,
  onKeyDown: externalKeyDown,
  placeholder,
  className = '',
}, ref) => {
  const { t } = useTranslation('chat');
  const resolvedPlaceholder = placeholder ?? t('messageInputPlaceholder');
  const divRef = useRef(null);
  const savedRangeRef = useRef(null); // last known cursor range before blur

  const applyTwemoji = useCallback(() => {
    if (!divRef.current || !twemoji?.parse) return;

    const sel = window.getSelection();
    let savedRange = null;
    if (sel && sel.rangeCount > 0 && divRef.current.contains(sel.anchorNode)) {
      savedRange = sel.getRangeAt(0).cloneRange();
    }

    twemoji.parse(divRef.current, TWEMOJI_OPTS);

    if (savedRange) {
      try {
        sel.removeAllRanges();
        sel.addRange(savedRange);
      } catch {
        moveCursorToEnd(divRef.current);
      }
    }
  }, []);

  // ─── Imperative Handle ──────────────────────────────────────────────────
  useImperativeHandle(ref, () => ({
    /** Insert emoji character at the current cursor position. */
    insertEmoji(emoji) {
      const el = divRef.current;
      if (!el) return;
      el.focus();

      const sel = window.getSelection();
      // If focus didn't restore a range inside el, try the saved pre-blur range
      if (savedRangeRef.current && (!sel.rangeCount || !el.contains(sel.anchorNode))) {
        try {
          sel.removeAllRanges();
          sel.addRange(savedRangeRef.current);
        } catch (_) {
          moveCursorToEnd(el);
        }
      }

      if (sel && sel.rangeCount > 0 && el.contains(sel.anchorNode)) {
        const range = sel.getRangeAt(0);
        range.deleteContents();
        const textNode = document.createTextNode(emoji);
        range.insertNode(textNode);
        range.setStartAfter(textNode);
        range.collapse(true);
        sel.removeAllRanges();
        sel.addRange(range);
      } else {
        el.appendChild(document.createTextNode(emoji));
        moveCursorToEnd(el);
      }

      const plain = extractPlainText(el);
      onTextChange(plain, getCaretCharOffset(el));
      applyTwemoji();
    },

    /** Insert arbitrary plain text at the current cursor (e.g. paste). */
    insertAtCursor(text) {
      const el = divRef.current;
      if (!el) return;
      el.focus();

      const sel = window.getSelection();
      if (savedRangeRef.current && (!sel.rangeCount || !el.contains(sel.anchorNode))) {
        try { sel.removeAllRanges(); sel.addRange(savedRangeRef.current); } catch (_) { moveCursorToEnd(el); }
      }

      if (sel && sel.rangeCount > 0 && el.contains(sel.anchorNode)) {
        const range = sel.getRangeAt(0);
        range.deleteContents();
        const textNode = document.createTextNode(text);
        range.insertNode(textNode);
        range.setStartAfter(textNode);
        range.collapse(true);
        sel.removeAllRanges();
        sel.addRange(range);
      } else {
        el.appendChild(document.createTextNode(text));
        moveCursorToEnd(el);
      }

      const plain = extractPlainText(el);
      onTextChange(plain, getCaretCharOffset(el));
      applyTwemoji();
    },

    /** Replace the entire content; optionally position cursor. */
    setValue(text, cursorPos) {
      const el = divRef.current;
      if (!el) return;
      el.innerHTML = '';
      if (text) el.appendChild(document.createTextNode(text));
      applyTwemoji();
      onTextChange(text ?? '', cursorPos ?? (text?.length ?? 0));
      if (cursorPos !== undefined) {
        requestAnimationFrame(() => setCaretAtOffset(el, cursorPos));
      }
    },

    /** Clear all content. */
    clear() {
      if (divRef.current) {
        divRef.current.innerHTML = '';
        onTextChange('', 0);
      }
    },

    /** Return current plain text (emoji chars, not img tags). */
    getText: () => extractPlainText(divRef.current || document.createElement('div')),

    focus: () => divRef.current?.focus(),

    // ── Cursor / selection helpers ────────────────────────────────────────

    /** Cursor start position in plain-text character units. */
    getSelectionStart: () => getCaretCharOffset(divRef.current),

    /** Cursor end position in plain-text character units. */
    getSelectionEnd: () => getCaretCharEnd(divRef.current),

    /** Move cursor to a plain-text character offset. */
    setCaretPosition(offset) {
      if (divRef.current) setCaretAtOffset(divRef.current, offset);
    },

    /** Plain text that is currently selected. */
    getSelectedText() {
      const el = divRef.current;
      if (!el) return '';
      const start = getCaretCharOffset(el);
      const end   = getCaretCharEnd(el);
      return extractPlainText(el).substring(start, end);
    },

    /** Delete the currently selected content. */
    deleteSelection() {
      const el = divRef.current;
      if (!el) return;
      const sel = window.getSelection();
      if (sel && sel.rangeCount > 0 && !sel.getRangeAt(0).collapsed) {
        sel.getRangeAt(0).deleteContents();
        const plain = extractPlainText(el);
        onTextChange(plain, getCaretCharOffset(el));
        applyTwemoji();
      }
    },
  }), [onTextChange, applyTwemoji]);

  // ─── Handlers ──────────────────────────────────────────────────────────
  const handleInput = useCallback(() => {
    if (!divRef.current) return;
    const plain = extractPlainText(divRef.current);
    const cursorPos = getCaretCharOffset(divRef.current);
    onTextChange(plain, cursorPos);
    applyTwemoji();
  }, [onTextChange, applyTwemoji]);

  const handleKeyDown = useCallback((e) => {
    // Let the parent handle keys first (e.g. @mention navigation in GroupChatView)
    externalKeyDown?.(e);
    if (e.defaultPrevented) return;

    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      onEnterPress?.();
    }
  }, [externalKeyDown, onEnterPress]);

  // Save cursor range on blur so insertEmoji/insertAtCursor can restore it
  const handleBlur = useCallback(() => {
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0 && divRef.current?.contains(sel.anchorNode)) {
      try { savedRangeRef.current = sel.getRangeAt(0).cloneRange(); } catch (_) {}
    }
  }, []);

  // Paste: plain text only — emoji chars will be parsed by applyTwemoji
  const handlePaste = useCallback((e) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text/plain');
    document.execCommand('insertText', false, text);
  }, []);

  return (
    <div className="w-full flex items-center">
      <div
        ref={divRef}
        contentEditable
        suppressContentEditableWarning
        onInput={handleInput}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        onBlur={handleBlur}
        data-placeholder={resolvedPlaceholder}
        className={`twemoji-input w-full bg-[var(--color-bg-muted)] border border-white/[0.07] rounded-full pl-4 pr-10 py-2 text-sm text-[var(--color-text-primary)] focus:outline-none focus:border-[#3d8ef0]/50 focus:ring-1 focus:ring-[#3d8ef0]/30 min-h-[36px] max-h-[120px] overflow-y-auto ${className}`}
        style={{ lineHeight: '1.4', wordBreak: 'break-word' }}
      />
    </div>
  );
});

TwemojiInput.displayName = 'TwemojiInput';
export default TwemojiInput;
